import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import {
  addRoom,
  getRoomsByFloor,
  deleteRoomById,
  updateRoomById,
} from "../../../actions/3dFusionAction";
import Fusion2DToolbar from "./Fusion2DToolbar";

const PGM_RESOLUTION = 0.05; // meters per pixel
const FEET_PER_METER = 3.28084;
const SQFT_PER_SQM = 10.7639;

const clampZoom = (z) => Math.min(Math.max(z, 0.1), 4);

const selectionTheme = {
  border: "#112138",
  labelBackground: "rgba(255, 255, 255, 0.92)",
  labelBorder: "#d6dce5",
  labelText: "#112138",
};

const measurementTheme = {
  color: "#112138",
  labelBorder: "#bac6d6",
};

const roundToSingleDecimal = (value) => Number(Number(value || 0).toFixed(1));

const createInitialFormData = () => ({
  roomName: "",
  roomType: "",
  floorType: "",
  carpetType: "",
  fixedFurniture: "",
  highTouchAreas: false,
  notes: "",
  width: 0,
  height: 0,
  area: 0,
  // Aramak specific data
  floorLevel: "",
  areaType: "",
  custodialSpaceType: "",
  custodialSpace: 0,
  locationID: "",
  custodialPriority: "",
  buildingCode: "",
  active: 1,
  hidden: 0,
  custodialTemplateCode: "",
});

const mapPolygonToFormData = (polygon) => ({
  roomName: polygon.roomName || "",
  roomType: polygon.roomType || "",
  floorType: polygon.floorType || "",
  carpetType: polygon.carpetType || "",
  fixedFurniture: polygon.fixedFurniture || "",
  highTouchAreas: polygon.highTouchAreas || false,
  notes: polygon.notes || "",
  width: polygon.width || 0,
  height: polygon.height || 0,
  area: polygon.area || 0,
  floorLevel: polygon.floorLevel || "",
  areaType: polygon.areaType || "",
  custodialSpaceType: polygon.custodialSpaceType || "",
  custodialSpace: polygon.custodialSpace || 0,
  locationID: polygon.locationID || "",
  custodialPriority: polygon.custodialPriority || "",
  buildingCode: polygon.buildingCode || "",
  active: polygon.active ?? 1,
  hidden: polygon.hidden ?? 0,
  custodialTemplateCode: polygon.custodialTemplateCode || "",
});

const extractJobIdFromFileUrl = (fileUrl) => {
  if (typeof fileUrl !== "string" || !fileUrl.trim()) return null;

  const sanitizedPath = fileUrl.trim().split("?")[0].split("#")[0];
  const segments = sanitizedPath.split("/").filter(Boolean);

  if (segments.length < 3) return null;

  return segments[segments.length - 3] || null;
};

const getPolygonAnchorPoint = (points = []) => {
  if (!Array.isArray(points) || points.length === 0) {
    return { x: 0, y: 0 };
  }

  const totals = points.reduce(
    (accumulator, point) => ({
      x: accumulator.x + Number(point?.x || 0),
      y: accumulator.y + Number(point?.y || 0),
    }),
    { x: 0, y: 0 },
  );

  return {
    x: totals.x / points.length,
    y: totals.y / points.length,
  };
};

const getRoomBadgeLayout = ({ points, textWidth, zoom }) => {
  const anchor = getPolygonAnchorPoint(points);
  const fontSize = 12 / zoom;
  const horizontalPadding = 8 / zoom;
  const verticalPadding = 5 / zoom;
  const badgeWidth = textWidth + horizontalPadding * 2;
  const badgeHeight = fontSize + verticalPadding * 2;

  return {
    fontSize,
    horizontalPadding,
    badgeWidth,
    badgeHeight,
    badgeX: anchor.x - badgeWidth / 2,
    badgeY: anchor.y - 22 / zoom,
  };
};

const getRoomToolboxLayout = ({ badgeLayout, zoom, overlayCtx }) => {
  const buttonDefinitions = [
    { id: "edit", label: "Edit", tooltip: "Adjust room vertices" },
    { id: "clear", label: "Clear", tooltip: "Remove room shape" },
    { id: "delete", label: "Delete", tooltip: "Delete saved room" },
  ];
  const fontSize = 11 / zoom;
  const buttonHeight = 24 / zoom;
  const horizontalPadding = 10 / zoom;
  const gap = 6 / zoom;
  const containerPadding = 5 / zoom;

  overlayCtx.save();
  overlayCtx.font = `600 ${fontSize}px sans-serif`;

  let currentX = containerPadding;
  const buttons = buttonDefinitions.map((button) => {
    const textWidth = overlayCtx.measureText(button.label).width;
    const width = textWidth + horizontalPadding * 2;
    const nextButton = {
      ...button,
      x: currentX,
      width,
      height: buttonHeight,
    };
    currentX += width + gap;
    return nextButton;
  });

  overlayCtx.restore();

  const containerWidth =
    buttons.length > 0
      ? buttons[buttons.length - 1].x +
        buttons[buttons.length - 1].width +
        containerPadding
      : 0;
  const containerHeight = buttonHeight + containerPadding * 2;

  return {
    x: badgeLayout.badgeX + badgeLayout.badgeWidth / 2 - containerWidth / 2,
    y: badgeLayout.badgeY - containerHeight - 8 / zoom,
    width: containerWidth,
    height: containerHeight,
    borderRadius: 10 / zoom,
    fontSize,
    buttonHeight,
    containerPadding,
    buttons: buttons.map((button) => ({
      ...button,
      x: button.x,
      y: containerPadding,
    })),
  };
};

const calculatePolygonMeasurements = (polygonPoints = [], resolution) => {
  if (!Array.isArray(polygonPoints) || polygonPoints.length < 3) {
    return { width: 0, height: 0, area: 0 };
  }

  const minX = Math.min(...polygonPoints.map((point) => point.x));
  const maxX = Math.max(...polygonPoints.map((point) => point.x));
  const minY = Math.min(...polygonPoints.map((point) => point.y));
  const maxY = Math.max(...polygonPoints.map((point) => point.y));

  let areaPixels = 0;

  for (let index = 0; index < polygonPoints.length; index += 1) {
    const current = polygonPoints[index];
    const next = polygonPoints[(index + 1) % polygonPoints.length];
    areaPixels += current.x * next.y - next.x * current.y;
  }

  const width = roundToSingleDecimal(
    (maxX - minX) * resolution * FEET_PER_METER,
  );
  const height = roundToSingleDecimal(
    (maxY - minY) * resolution * FEET_PER_METER,
  );
  const areaSqMeters = Math.abs(areaPixels / 2) * resolution * resolution;

  return {
    width,
    height,
    area: roundToSingleDecimal(areaSqMeters * SQFT_PER_SQM),
  };
};

const Fusion2DModel = ({
  fileUrl,
  scanFileUrl,
  scanJobId,
  selectedTool,
  setSelected2DTool,
  onError,
}) => {
  const floorID = useSelector((state) => state.floor.data.id);
  const building = useSelector((state) => state.building.data);
  const floor = useSelector((state) => state.floor.data);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const offscreenRef = useRef(null);
  const pointerDownRef = useRef(null);

  const [pgm, setPgm] = useState(null);
  const [zoom, setZoom] = useState(0.2);
  const [resolution, setResolution] = useState(PGM_RESOLUTION);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const [points, setPoints] = useState([]);
  const [polygonClosed, setPolygonClosed] = useState(false);
  const [polygonColor, setPolygonColor] = useState(null);
  const [hoverFirst, setHoverFirst] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(createInitialFormData);
  const [savedPolygons, setSavedPolygons] = useState([]);
  const [selectedPolygonId, setSelectedPolygonId] = useState(null);
  const [hoveredPolygonId, setHoveredPolygonId] = useState(null);
  const [editablePolygonPoints, setEditablePolygonPoints] = useState([]);
  const [draggingVertexIndex, setDraggingVertexIndex] = useState(null);
  const [isReplacingSelectedPolygon, setIsReplacingSelectedPolygon] =
    useState(false);
  const [hoveredToolboxAction, setHoveredToolboxAction] = useState(null);
  const [selectedRoomToolMode, setSelectedRoomToolMode] = useState(null);
  const [roomPendingDelete, setRoomPendingDelete] = useState(null);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [measurementHoverPoint, setMeasurementHoverPoint] = useState(null);

  const buildRoomPayload = useCallback(
    (polygon) => {
      const currentFileUrl = typeof fileUrl === "string" ? fileUrl : "";
      const selectedScanFileUrl =
        typeof scanFileUrl === "string" ? scanFileUrl : "";
      const resolvedJobId =
        scanJobId ||
        extractJobIdFromFileUrl(selectedScanFileUrl) ||
        extractJobIdFromFileUrl(currentFileUrl);

      return {
        room: polygon.roomName || "Untitled",
        floors_id: floorID || null,
        room_type: polygon.roomType || "",
        room_area: Number(polygon.area) || 0,
        floor_type: polygon.floorType || "",
        carpet_type: polygon.carpetType || "",
        fixed_furniture: polygon.fixedFurniture || "",
        high_touch_area: polygon.highTouchAreas || false,
        note: polygon.notes || "",
        points: polygon.points || [],
        file_url: selectedScanFileUrl || currentFileUrl,
        job_id: resolvedJobId || null,
        floor_level: polygon.floorLevel,
        area_type: polygon.areaType,
        custodial_space_type: polygon.custodialSpaceType,
        custodial_space: polygon.custodialSpace,
        location_id: polygon.locationID,
        custodial_priority: polygon.custodialPriority,
        building_code: polygon.buildingCode,
        active: polygon.active,
        hidden: polygon.hidden,
        custodial_template_code: polygon.custodialTemplateCode,
      };
    },
    [floorID, fileUrl, scanFileUrl, scanJobId],
  );

  const isDrawTool = selectedTool === "draw";
  const isPanZoomTool = selectedTool === "panzoom";
  const isHandTool = selectedTool === "hand";
  const isEditTool = selectedTool === "edit";
  const isMeasureTool = selectedTool === "measure";
  const isVertexEditTool =
    (isHandTool || isEditTool) && selectedRoomToolMode === "edit";

  const getSelectedPolygonRenderData = useCallback(() => {
    if (!selectedPolygonId) {
      return null;
    }

    const selectedPolygon = savedPolygons.find(
      (polygon) => polygon.id === selectedPolygonId,
    );

    if (!selectedPolygon) {
      return null;
    }

    const renderedPoints = isReplacingSelectedPolygon
      ? editablePolygonPoints
      : editablePolygonPoints.length >= 3
        ? editablePolygonPoints
        : selectedPolygon.points;

    if (!Array.isArray(renderedPoints) || renderedPoints.length < 3) {
      return null;
    }

    return {
      polygon: selectedPolygon,
      points: renderedPoints,
    };
  }, [
    editablePolygonPoints,
    isReplacingSelectedPolygon,
    savedPolygons,
    selectedPolygonId,
  ]);

  const getSelectedPolygonToolboxLayout = useCallback(() => {
    if (
      !overlayRef.current ||
      !selectedPolygonId ||
      isReplacingSelectedPolygon
    ) {
      return null;
    }

    const selectedPolygonData = getSelectedPolygonRenderData();
    if (!selectedPolygonData) {
      return null;
    }

    const overlayCtx = overlayRef.current.getContext("2d");
    const fontSize = 12 / zoom;
    const labelText = selectedPolygonData.polygon.roomName || "Untitled";

    overlayCtx.save();
    overlayCtx.font = `600 ${fontSize}px sans-serif`;
    const textWidth = overlayCtx.measureText(labelText).width;
    overlayCtx.restore();

    const badgeLayout = getRoomBadgeLayout({
      points: selectedPolygonData.points,
      textWidth,
      zoom,
    });

    return getRoomToolboxLayout({
      badgeLayout,
      zoom,
      overlayCtx,
    });
  }, [
    getSelectedPolygonRenderData,
    isReplacingSelectedPolygon,
    selectedPolygonId,
    zoom,
  ]);

  // PGM Parser
  const parsePGM = (u8) => {
    // Read the header with metadata from the first 1024 bytes
    const text = new TextDecoder().decode(u8.slice(0, 1024));
    console.log("test header text", text);
    if (!text.startsWith("P5")) throw new Error("Only P5 supported");

    // Read all the Unit8Array
    const tokens = [];
    let current = "";
    for (let i = 0; i < u8.length; i++) {
      const ch = String.fromCharCode(u8[i]);
      if (ch === "#")
        while (i < u8.length && String.fromCharCode(u8[i]) !== "\n") i++;
      else if (/\s/.test(ch)) {
        if (current) {
          tokens.push(current);
          current = "";
        }
      } else current += ch;
      if (tokens.length >= 4) break;
    }

    /*
    Parsing
      tokens[0]: P5
      tokens[1]: image width
      tokens[2]: image height
      tokens[3]: max pixel value
    */
    console.log("test sample tokens", tokens);
    const width = parseInt(tokens[1], 10);
    const height = parseInt(tokens[2], 10);
    const maxval = parseInt(tokens[3], 10);
    let pixelStart = text.indexOf(tokens[3]) + tokens[3].length;
    while (u8[pixelStart] <= 32) pixelStart++;
    const pixels = new Uint8Array(width * height);
    pixels.set(u8.slice(pixelStart, pixelStart + width * height));
    return { width, height, maxval, pixels };
  };

  const randomColor = () =>
    `rgba(${Math.random() * 200},${Math.random() * 200},${Math.random() * 200},0.5)`;

  // Load PGM
  const loadPGM = useCallback(
    async (url) => {
      try {
        const res = await fetch(url);

        const resolutionHeader = res.headers.get("X-Resolution");
        if (resolutionHeader) {
          const parsedResolution = parseFloat(resolutionHeader);
          setResolution(
            isNaN(parsedResolution) ? PGM_RESOLUTION : parsedResolution,
          );
        } else {
          setResolution(PGM_RESOLUTION);
        }

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const buffer = await res.arrayBuffer();
        setPgm(parsePGM(new Uint8Array(buffer)));
        onError?.("");
      } catch (error) {
        setPgm(null);
        onError?.(
          "Unable to load 2D scan file. Please contact administrator for support.",
        );
      }
    },
    [onError],
  );

  // Draw loop (map + overlay)
  const redraw = useCallback(() => {
    if (!mapRef.current || !overlayRef.current || !pgm) return;
    const mapCtx = mapRef.current.getContext("2d");
    const overlayCtx = overlayRef.current.getContext("2d");

    mapCtx.setTransform(1, 0, 0, 1, 0, 0);
    overlayCtx.setTransform(1, 0, 0, 1, 0, 0);
    mapCtx.clearRect(0, 0, mapRef.current.width, mapRef.current.height);
    overlayCtx.clearRect(
      0,
      0,
      overlayRef.current.width,
      overlayRef.current.height,
    );

    mapCtx.setTransform(zoom, 0, 0, zoom, pan.x, pan.y);
    overlayCtx.setTransform(zoom, 0, 0, zoom, pan.x, pan.y);

    if (offscreenRef.current) mapCtx.drawImage(offscreenRef.current, 0, 0);

    // Draw non-selected polygons first, then selected on top for clearer focus.
    const visiblePolygons = savedPolygons.filter((poly) => !poly.hidden);

    const nonSelectedPolygons = visiblePolygons.filter(
      (poly) => poly.id !== selectedPolygonId,
    );
    const selectedPolygons = visiblePolygons.filter(
      (poly) => poly.id === selectedPolygonId,
    );

    [...nonSelectedPolygons, ...selectedPolygons].forEach((poly) => {
      const isSelected = poly.id === selectedPolygonId;
      const isHovered = poly.id === hoveredPolygonId;
      const renderedPoints =
        isSelected && isReplacingSelectedPolygon
          ? editablePolygonPoints
          : isSelected && editablePolygonPoints.length >= 3
            ? editablePolygonPoints
            : poly.points;

      if (!Array.isArray(renderedPoints) || renderedPoints.length < 3) {
        return;
      }

      overlayCtx.save();
      overlayCtx.beginPath();
      overlayCtx.moveTo(renderedPoints[0].x, renderedPoints[0].y);
      renderedPoints.forEach((p, i) => i > 0 && overlayCtx.lineTo(p.x, p.y));
      overlayCtx.closePath();

      if (selectedPolygonId && !isSelected) {
        overlayCtx.globalAlpha = 0.3;
      }

      overlayCtx.fillStyle = poly.color;
      overlayCtx.fill();

      if (isSelected) {
        overlayCtx.strokeStyle = selectionTheme.border;
        overlayCtx.lineWidth = 3 / zoom;
        overlayCtx.stroke();
      } else if (isHovered) {
        overlayCtx.setLineDash([8 / zoom, 6 / zoom]);
        overlayCtx.strokeStyle = "#556987";
        overlayCtx.lineWidth = 2 / zoom;
        overlayCtx.stroke();
      } else {
        overlayCtx.strokeStyle = "#d14343";
        overlayCtx.lineWidth = 2 / zoom;
        overlayCtx.stroke();
      }

      const labelText = poly.roomName || "Untitled";
      const fontSize = 12 / zoom;

      overlayCtx.font = `600 ${fontSize}px sans-serif`;
      const textWidth = overlayCtx.measureText(labelText).width;
      const badgeLayout = getRoomBadgeLayout({
        points: renderedPoints,
        textWidth,
        zoom,
      });

      overlayCtx.beginPath();
      overlayCtx.fillStyle = selectionTheme.labelBackground;
      overlayCtx.strokeStyle = isSelected
        ? selectionTheme.border
        : selectionTheme.labelBorder;
      overlayCtx.lineWidth = (isSelected ? 2 : 1) / zoom;
      overlayCtx.roundRect(
        badgeLayout.badgeX,
        badgeLayout.badgeY,
        badgeLayout.badgeWidth,
        badgeLayout.badgeHeight,
        8 / zoom,
      );
      overlayCtx.fill();
      overlayCtx.stroke();

      overlayCtx.fillStyle = selectionTheme.labelText;
      overlayCtx.textBaseline = "middle";
      overlayCtx.fillText(
        labelText,
        badgeLayout.badgeX + badgeLayout.horizontalPadding,
        badgeLayout.badgeY + badgeLayout.badgeHeight / 2,
      );

      if (isSelected && !isReplacingSelectedPolygon) {
        const toolboxLayout = getRoomToolboxLayout({
          badgeLayout,
          zoom,
          overlayCtx,
        });

        overlayCtx.beginPath();
        overlayCtx.fillStyle = "rgba(255, 255, 255, 0.96)";
        overlayCtx.strokeStyle = selectionTheme.labelBorder;
        overlayCtx.lineWidth = 1 / zoom;
        overlayCtx.roundRect(
          toolboxLayout.x,
          toolboxLayout.y,
          toolboxLayout.width,
          toolboxLayout.height,
          toolboxLayout.borderRadius,
        );
        overlayCtx.fill();
        overlayCtx.stroke();

        toolboxLayout.buttons.forEach((button) => {
          const isHovered = hoveredToolboxAction === button.id;
          const isActive =
            button.id === "edit" && selectedRoomToolMode === "edit";
          const buttonX = toolboxLayout.x + button.x;
          const buttonY = toolboxLayout.y + button.y;

          overlayCtx.beginPath();
          overlayCtx.fillStyle =
            isActive || isHovered ? "#eef2f7" : "transparent";
          overlayCtx.roundRect(
            buttonX,
            buttonY,
            button.width,
            button.height,
            7 / zoom,
          );
          overlayCtx.fill();

          overlayCtx.fillStyle =
            button.id === "delete"
              ? "#a54545"
              : isActive
                ? selectionTheme.border
                : selectionTheme.labelText;
          overlayCtx.font = `600 ${toolboxLayout.fontSize}px sans-serif`;
          overlayCtx.textAlign = "center";
          overlayCtx.textBaseline = "middle";
          overlayCtx.fillText(
            button.label,
            buttonX + button.width / 2,
            buttonY + button.height / 2,
          );
        });

        const hoveredButton = toolboxLayout.buttons.find(
          (button) => button.id === hoveredToolboxAction,
        );

        if (hoveredButton?.tooltip) {
          const tooltipFontSize = 11 / zoom;
          const tooltipHorizontalPadding = 8 / zoom;
          const tooltipVerticalPadding = 4 / zoom;
          const tooltipHeight = tooltipFontSize + tooltipVerticalPadding * 2;
          const tooltipGap = 6 / zoom;
          const tooltipArrowSize = 5 / zoom;
          const tooltipCenterX =
            toolboxLayout.x + hoveredButton.x + hoveredButton.width / 2;

          overlayCtx.font = `400 ${tooltipFontSize}px sans-serif`;
          const tooltipTextWidth = overlayCtx.measureText(
            hoveredButton.tooltip,
          ).width;
          const tooltipWidth = tooltipTextWidth + tooltipHorizontalPadding * 2;
          const tooltipX = tooltipCenterX - tooltipWidth / 2;
          const tooltipY = toolboxLayout.y - tooltipHeight - tooltipGap;
          const tooltipBackground = "rgba(97, 97, 97, 0.92)";

          overlayCtx.beginPath();
          overlayCtx.fillStyle = tooltipBackground;
          overlayCtx.roundRect(
            tooltipX,
            tooltipY,
            tooltipWidth,
            tooltipHeight,
            4 / zoom,
          );
          overlayCtx.fill();

          overlayCtx.beginPath();
          overlayCtx.moveTo(
            tooltipCenterX - tooltipArrowSize,
            toolboxLayout.y - tooltipGap,
          );
          overlayCtx.lineTo(
            tooltipCenterX + tooltipArrowSize,
            toolboxLayout.y - tooltipGap,
          );
          overlayCtx.lineTo(
            tooltipCenterX,
            toolboxLayout.y - tooltipGap + tooltipArrowSize,
          );
          overlayCtx.closePath();
          overlayCtx.fill();

          overlayCtx.fillStyle = "#ffffff";
          overlayCtx.font = `400 ${tooltipFontSize}px sans-serif`;
          overlayCtx.textAlign = "center";
          overlayCtx.textBaseline = "middle";
          overlayCtx.fillText(
            hoveredButton.tooltip,
            tooltipCenterX,
            tooltipY + tooltipHeight / 2,
          );
        }

        overlayCtx.textAlign = "start";
      }

      if (isSelected && isVertexEditTool && renderedPoints.length >= 3) {
        renderedPoints.forEach((point, index) => {
          overlayCtx.beginPath();
          overlayCtx.arc(point.x, point.y, 5 / zoom, 0, Math.PI * 2);
          overlayCtx.fillStyle =
            index === draggingVertexIndex ? "#112138" : "#ffffff";
          overlayCtx.strokeStyle = selectionTheme.border;
          overlayCtx.lineWidth = 2 / zoom;
          overlayCtx.fill();
          overlayCtx.stroke();
        });
      }

      overlayCtx.restore();
    });

    // Current polygon
    if (points.length > 0) {
      overlayCtx.beginPath();
      overlayCtx.moveTo(points[0].x, points[0].y);
      points.forEach((p, i) => i > 0 && overlayCtx.lineTo(p.x, p.y));
      if (polygonClosed) overlayCtx.closePath();
      overlayCtx.fillStyle = polygonClosed ? polygonColor : "transparent";
      overlayCtx.strokeStyle = "red";
      overlayCtx.lineWidth = 2 / zoom;
      overlayCtx.fill();
      overlayCtx.stroke();

      // Draw points
      points.forEach((pt, i) => {
        overlayCtx.beginPath();
        overlayCtx.arc(pt.x, pt.y, 4 / zoom, 0, Math.PI * 2);
        overlayCtx.fillStyle =
          i === 0 && !polygonClosed ? (hoverFirst ? "green" : "red") : "red";
        overlayCtx.fill();
      });
    }

    // Measurement line
    if (measurementPoints.length > 0) {
      const startPoint = measurementPoints[0];
      const endPoint =
        measurementPoints.length >= 2
          ? measurementPoints[1]
          : measurementHoverPoint;

      overlayCtx.save();

      overlayCtx.beginPath();
      overlayCtx.arc(startPoint.x, startPoint.y, 5 / zoom, 0, Math.PI * 2);
      overlayCtx.fillStyle = measurementTheme.color;
      overlayCtx.fill();

      if (endPoint) {
        const lineDx = endPoint.x - startPoint.x;
        const lineDy = endPoint.y - startPoint.y;
        const lineLengthPixels = Math.hypot(lineDx, lineDy);
        const unitX = lineLengthPixels > 0 ? lineDx / lineLengthPixels : 0;
        const unitY = lineLengthPixels > 0 ? lineDy / lineLengthPixels : 0;
        const normalX = -unitY;
        const normalY = unitX;
        const tickSpacingPixels = Math.max(8, 1 / resolution);

        // Ruler spine
        overlayCtx.beginPath();
        overlayCtx.moveTo(startPoint.x, startPoint.y);
        overlayCtx.lineTo(endPoint.x, endPoint.y);
        overlayCtx.strokeStyle = measurementTheme.color;
        overlayCtx.lineWidth = 2 / zoom;

        if (measurementPoints.length < 2) {
          overlayCtx.setLineDash([10 / zoom, 6 / zoom]);
        }

        overlayCtx.stroke();
        overlayCtx.setLineDash([]);

        // Ruler ticks
        if (lineLengthPixels > 0) {
          for (
            let distancePixels = 0;
            distancePixels <= lineLengthPixels;
            distancePixels += tickSpacingPixels
          ) {
            const tickRatio = distancePixels / lineLengthPixels;
            const tickBaseX = startPoint.x + lineDx * tickRatio;
            const tickBaseY = startPoint.y + lineDy * tickRatio;
            const isMajorTick =
              Math.round(distancePixels / tickSpacingPixels) % 5 === 0;
            const tickLength = (isMajorTick ? 10 : 6) / zoom;

            overlayCtx.beginPath();
            overlayCtx.moveTo(tickBaseX, tickBaseY);
            overlayCtx.lineTo(
              tickBaseX + normalX * tickLength,
              tickBaseY + normalY * tickLength,
            );
            overlayCtx.strokeStyle = measurementTheme.color;
            overlayCtx.lineWidth = (isMajorTick ? 1.8 : 1.2) / zoom;
            overlayCtx.stroke();
          }
        }

        overlayCtx.beginPath();
        overlayCtx.arc(endPoint.x, endPoint.y, 5 / zoom, 0, Math.PI * 2);
        overlayCtx.fillStyle =
          measurementPoints.length >= 2 ? measurementTheme.color : "#ffffff";
        overlayCtx.strokeStyle = measurementTheme.color;
        overlayCtx.lineWidth = 2 / zoom;
        overlayCtx.fill();
        overlayCtx.stroke();

        const lineLengthMeters = lineLengthPixels * resolution;
        const lineLengthFeet = lineLengthMeters * FEET_PER_METER;
        const labelText = `${lineLengthFeet.toFixed(1)} ft`;
        const fontSize = 12 / zoom;
        const labelPaddingX = 8 / zoom;
        const labelPaddingY = 4 / zoom;
        const labelCenterX = (startPoint.x + endPoint.x) / 2;
        const labelCenterY = (startPoint.y + endPoint.y) / 2 - 12 / zoom;

        overlayCtx.font = `600 ${fontSize}px sans-serif`;
        const labelWidth =
          overlayCtx.measureText(labelText).width + labelPaddingX * 2;
        const labelHeight = fontSize + labelPaddingY * 2;

        overlayCtx.beginPath();
        overlayCtx.fillStyle = "rgba(255, 255, 255, 0.95)";
        overlayCtx.strokeStyle = measurementTheme.labelBorder;
        overlayCtx.lineWidth = 1 / zoom;
        overlayCtx.roundRect(
          labelCenterX - labelWidth / 2,
          labelCenterY - labelHeight / 2,
          labelWidth,
          labelHeight,
          6 / zoom,
        );
        overlayCtx.fill();
        overlayCtx.stroke();

        overlayCtx.fillStyle = measurementTheme.color;
        overlayCtx.textAlign = "center";
        overlayCtx.textBaseline = "middle";
        overlayCtx.fillText(labelText, labelCenterX, labelCenterY);
      }

      overlayCtx.restore();
    }
  }, [
    pgm,
    zoom,
    pan,
    points,
    editablePolygonPoints,
    draggingVertexIndex,
    isReplacingSelectedPolygon,
    savedPolygons,
    polygonClosed,
    polygonColor,
    hoverFirst,
    selectedPolygonId,
    selectedTool,
    hoveredToolboxAction,
    selectedRoomToolMode,
    measurementPoints,
    measurementHoverPoint,
    resolution,
  ]);

  // FloorName
  const floorName = useMemo(() => {
    return floor.floor;
  }, [floor]);

  // Building Name'
  const buildingName = useMemo(() => {
    return building.name;
  }, [building]);

  // Calculate Polygon Area
  const calculatePolygonAreaPixels = (points) => {
    if (!Array.isArray(points) || points.length < 3) return 0;

    let area = 0;

    for (let i = 0; i < points.length; i++) {
      const current = points[i];
      const next = points[(i + 1) % points.length];
      area += current.x * next.y - next.x * current.y;
    }
    return Math.abs(area / 2);
  };

  // Initial Load
  useEffect(() => {
    if (floorID) {
      void handleLoadRoomsFromBackend();
    }
  }, [floorID]);

  // Center the PGM map once both canvas size and pgm data are ready
  const hasCenteredRef = useRef(false);
  useEffect(() => {
    if (!pgm || canvasSize.width === 0 || canvasSize.height === 0) return;
    if (hasCenteredRef.current) return;
    hasCenteredRef.current = true;
    setPan({
      x: (canvasSize.width - pgm.width * zoom) / 2,
      y: (canvasSize.height - pgm.height * zoom) / 2,
    });
  }, [pgm, canvasSize]);

  useEffect(() => {
    if (fileUrl) loadPGM(fileUrl);
  }, [fileUrl, loadPGM]);

  useEffect(() => {
    setSavedPolygons([]);
    setPoints([]);
    setPolygonClosed(false);
    setPolygonColor(null);
    setSelectedPolygonId(null);
    setHoveredPolygonId(null);
    setHoveredToolboxAction(null);
    setShowForm(false);
    setFormData(createInitialFormData());
    setEditablePolygonPoints([]);
    setDraggingVertexIndex(null);
    setIsReplacingSelectedPolygon(false);
    onError?.("");
  }, [scanJobId, scanFileUrl]);

  // Offscreen Map
  useEffect(() => {
    if (!pgm) return;
    const off = document.createElement("canvas");
    off.width = pgm.width;
    off.height = pgm.height;
    const ctx = off.getContext("2d");
    const img = ctx.createImageData(pgm.width, pgm.height);
    for (let i = 0; i < pgm.width * pgm.height; i++) {
      const val = Math.round((pgm.pixels[i] / pgm.maxval) * 255);
      img.data[i * 4] = val;
      img.data[i * 4 + 1] = val;
      img.data[i * 4 + 2] = val;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    offscreenRef.current = off;
  }, [pgm]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (!containerRef.current) return;
      setCanvasSize({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    };

    updateCanvasSize();

    window.addEventListener("resize", updateCanvasSize);
    return () => window.removeEventListener("resize", updateCanvasSize);
  }, []);

  useEffect(() => {
    if (selectedTool === "save") {
      void handleSavePolygon();
    }
  }, [selectedTool]);

  useEffect(() => {
    if (selectedTool === "draw") return;

    setPoints([]);
    setPolygonClosed(false);
    setPolygonColor(null);
    setHoverFirst(false);

    if (!selectedPolygonId) {
      setShowForm(false);
      setFormData(createInitialFormData());
    }
  }, [selectedPolygonId, selectedTool]);

  useEffect(() => {
    if (selectedTool !== "measure") {
      setMeasurementPoints([]);
      setMeasurementHoverPoint(null);
    }
  }, [selectedTool]);

  useEffect(() => {
    const handleOutsideMouseDown = (event) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target)) return;

      setMeasurementPoints([]);
      setMeasurementHoverPoint(null);
    };

    document.addEventListener("mousedown", handleOutsideMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsideMouseDown);
    };
  }, []);

  useEffect(() => {
    if (!selectedPolygonId) {
      setEditablePolygonPoints([]);
      setDraggingVertexIndex(null);
      setSelectedRoomToolMode(null);
      setIsReplacingSelectedPolygon(false);
      return;
    }

    if (isReplacingSelectedPolygon) {
      return;
    }

    const selectedPolygon = savedPolygons.find(
      (polygon) => polygon.id === selectedPolygonId,
    );

    if (!selectedPolygon) {
      setEditablePolygonPoints([]);
      setDraggingVertexIndex(null);
      setSelectedRoomToolMode(null);
      setIsReplacingSelectedPolygon(false);
      return;
    }

    setEditablePolygonPoints(selectedPolygon.points);
  }, [isReplacingSelectedPolygon, savedPolygons, selectedPolygonId]);

  useEffect(() => {
    if (!selectedPolygonId || editablePolygonPoints.length < 3) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...calculatePolygonMeasurements(editablePolygonPoints, resolution),
    }));
  }, [editablePolygonPoints, resolution, selectedPolygonId]);

  const isPointInPolygon = (x, y, points) => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x;
      const yi = points[i].y;
      const xj = points[j].x;
      const yj = points[j].y;

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersect) inside = !inside;
    }
    return inside;
  };

  const getMousePos = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
  };

  const findPolygonAtPosition = useCallback(
    (x, y) => {
      for (let index = savedPolygons.length - 1; index >= 0; index -= 1) {
        const polygon = savedPolygons[index];
        const polygonPoints =
          polygon.id === selectedPolygonId && isReplacingSelectedPolygon
            ? []
            : polygon.id === selectedPolygonId &&
                editablePolygonPoints.length >= 3
              ? editablePolygonPoints
              : polygon.points;
        if (polygon.hidden) continue;
        if (isPointInPolygon(x, y, polygonPoints)) {
          return {
            ...polygon,
            points: polygonPoints,
          };
        }
      }

      return null;
    },
    [
      editablePolygonPoints,
      isReplacingSelectedPolygon,
      savedPolygons,
      selectedPolygonId,
    ],
  );

  const getVertexIndexAtPosition = useCallback(
    (polygonPoints, x, y) => {
      if (!Array.isArray(polygonPoints) || polygonPoints.length < 3) {
        return -1;
      }

      for (let index = polygonPoints.length - 1; index >= 0; index -= 1) {
        const point = polygonPoints[index];
        if (Math.hypot(point.x - x, point.y - y) <= 10 / zoom) {
          return index;
        }
      }

      return -1;
    },
    [zoom],
  );

  const selectPolygonFromCanvas = useCallback(
    (e) => {
      const { x, y } = getMousePos(e);
      const clickedPolygon = findPolygonAtPosition(x, y);

      if (!clickedPolygon) {
        setSelectedPolygonId(null);
        setHoveredPolygonId(null);
        setHoveredToolboxAction(null);
        setSelectedRoomToolMode(null);
        if (!points.length || selectedPolygonId) {
          setShowForm(false);
          setFormData(createInitialFormData());
        }
        return false;
      }

      setSelectedPolygonId(clickedPolygon.id);
      setHoveredPolygonId(clickedPolygon.id);
      setHoveredToolboxAction(null);
      setSelectedRoomToolMode(null);
      setFormData(mapPolygonToFormData(clickedPolygon));
      setShowForm(true);
      setPoints([]);
      setPolygonClosed(false);

      return true;
    },
    [findPolygonAtPosition, points.length, selectedPolygonId, pan, zoom],
  );

  // Mouse events
  const handleMouseDown = (e) => {
    if (!overlayRef.current) return;
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    const rect = overlayRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    const toolboxLayout = getSelectedPolygonToolboxLayout();
    if (toolboxLayout) {
      const clickedButton = toolboxLayout.buttons.find((button) => {
        const buttonX = toolboxLayout.x + button.x;
        const buttonY = toolboxLayout.y + button.y;

        return (
          x >= buttonX &&
          x <= buttonX + button.width &&
          y >= buttonY &&
          y <= buttonY + button.height
        );
      });

      if (clickedButton) {
        pointerDownRef.current = null;

        if (clickedButton.id === "edit" && selectedPolygonId) {
          handleSelectRoom(selectedPolygonId);
          setSelectedRoomToolMode("edit");
        }

        if (clickedButton.id === "clear") {
          handleClearPolygon();
        }

        if (clickedButton.id === "delete") {
          handleRequestDeletePolygon(selectedPolygonId);
        }

        return;
      }
    }

    if (isDrawTool) {
      const canSelectExistingRoom = points.length === 0 || polygonClosed;
      if (canSelectExistingRoom) {
        const clickedPolygon = findPolygonAtPosition(x, y);
        if (clickedPolygon) {
          handleSelectRoom(clickedPolygon.id);
          return;
        }
      }

      if (polygonClosed) {
        setPoints([{ x, y }]);
        setPolygonClosed(false);
        setPolygonColor(null);
        setHoverFirst(false);
        setSelectedPolygonId(null);
        setHoveredPolygonId(null);
        setHoveredToolboxAction(null);
        setSelectedRoomToolMode(null);
        setEditablePolygonPoints([]);
        setDraggingVertexIndex(null);
        setIsReplacingSelectedPolygon(false);
        setShowForm(false);
        setFormData(createInitialFormData());
        return;
      }

      if (!polygonClosed && points.length > 2) {
        const first = points[0];
        const dist = Math.hypot(first.x - x, first.y - y);
        if (dist < 10 / zoom) {
          setPolygonClosed(true);
          setPolygonColor(randomColor());
          setShowForm(true);

          // Calculate bounding box
          const minX = Math.min(...points.map((p) => p.x));
          const maxX = Math.max(...points.map((p) => p.x));
          const minY = Math.min(...points.map((p) => p.y));
          const maxY = Math.max(...points.map((p) => p.y));

          const widthFeet = roundToSingleDecimal(
            (maxX - minX) * resolution * FEET_PER_METER,
          );
          const heightFeet = roundToSingleDecimal(
            (maxY - minY) * resolution * FEET_PER_METER,
          );

          const areaPixels = calculatePolygonAreaPixels(points);
          const areaSqMeters = areaPixels * resolution * resolution;
          const areaSqFeet = roundToSingleDecimal(areaSqMeters * SQFT_PER_SQM);

          setFormData((prev) => ({
            ...prev,
            width: widthFeet,
            height: heightFeet,
            area: areaSqFeet,
          }));

          return;
        }
      }
      if (!polygonClosed) setPoints((prev) => [...prev, { x, y }]);
      return;
    }

    if (isMeasureTool) {
      const { x: mouseX, y: mouseY } = getMousePos(e);

      if (measurementPoints.length >= 2) {
        setMeasurementPoints([{ x: mouseX, y: mouseY }]);
        setMeasurementHoverPoint(null);
        return;
      }

      if (measurementPoints.length === 1) {
        setMeasurementPoints((prev) => [...prev, { x: mouseX, y: mouseY }]);
        setMeasurementHoverPoint(null);
        return;
      }

      setMeasurementPoints([{ x: mouseX, y: mouseY }]);
      setMeasurementHoverPoint(null);
      return;
    }

    if (isPanZoomTool) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if (isHandTool) {
      const { x: mouseX, y: mouseY } = getMousePos(e);
      if (selectedPolygonId && editablePolygonPoints.length >= 3) {
        const vertexIndex = getVertexIndexAtPosition(
          editablePolygonPoints,
          mouseX,
          mouseY,
        );

        if (vertexIndex >= 0) {
          setDraggingVertexIndex(vertexIndex);
          return;
        }
      }

      return;
    }

    if (isEditTool) {
      const { x: mouseX, y: mouseY } = getMousePos(e);
      if (selectedPolygonId && editablePolygonPoints.length >= 3) {
        const vertexIndex = getVertexIndexAtPosition(
          editablePolygonPoints,
          mouseX,
          mouseY,
        );

        if (vertexIndex >= 0) {
          setDraggingVertexIndex(vertexIndex);
          return;
        }
      }

      selectPolygonFromCanvas(e);
      return;
    }

    return;
  };

  const handleMouseMove = (e) => {
    if (isMeasureTool) {
      if (measurementPoints.length === 1) {
        const { x, y } = getMousePos(e);
        setMeasurementHoverPoint({ x, y });
      }
      return;
    }

    if (isDrawTool) {
      if (!polygonClosed && points.length > 0) {
        const { x, y } = getMousePos(e);
        const first = points[0];
        setHoverFirst(Math.hypot(first.x - x, first.y - y) < 10 / zoom);
      }
      return;
    }

    if (
      (isHandTool || isEditTool) &&
      draggingVertexIndex === null &&
      !isDragging
    ) {
      const { x, y } = getMousePos(e);
      let nextHoveredPolygonId = null;
      let nextHoveredToolboxAction = null;

      const toolboxLayout = getSelectedPolygonToolboxLayout();
      if (toolboxLayout) {
        const hoveredButton = toolboxLayout.buttons.find((button) => {
          const buttonX = toolboxLayout.x + button.x;
          const buttonY = toolboxLayout.y + button.y;

          return (
            x >= buttonX &&
            x <= buttonX + button.width &&
            y >= buttonY &&
            y <= buttonY + button.height
          );
        });

        nextHoveredToolboxAction = hoveredButton?.id || null;
      }

      for (const polygon of savedPolygons) {
        if (polygon.hidden) continue;
        if (polygon.id === selectedPolygonId && isReplacingSelectedPolygon) {
          continue;
        }

        const polygonPoints =
          polygon.id === selectedPolygonId && editablePolygonPoints.length >= 3
            ? editablePolygonPoints
            : polygon.points;

        if (isPointInPolygon(x, y, polygonPoints)) {
          nextHoveredPolygonId = polygon.id;
          break;
        }
      }

      setHoveredPolygonId((currentId) =>
        currentId === nextHoveredPolygonId ? currentId : nextHoveredPolygonId,
      );
      setHoveredToolboxAction((currentAction) =>
        currentAction === nextHoveredToolboxAction
          ? currentAction
          : nextHoveredToolboxAction,
      );
    }

    if (isVertexEditTool && draggingVertexIndex !== null) {
      const { x, y } = getMousePos(e);
      setEditablePolygonPoints((prev) =>
        prev.map((point, index) =>
          index === draggingVertexIndex ? { x, y } : point,
        ),
      );
      return;
    }

    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };
  const handleMouseUp = (e) => {
    if (isMeasureTool) {
      pointerDownRef.current = null;
      return;
    }

    const pointerDown = pointerDownRef.current;
    const didClick =
      pointerDown &&
      Math.hypot(e.clientX - pointerDown.x, e.clientY - pointerDown.y) < 5;

    setIsDragging(false);
    pointerDownRef.current = null;
    setDraggingVertexIndex(null);

    if ((isHandTool || isEditTool) && didClick) {
      selectPolygonFromCanvas(e);
    }
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    pointerDownRef.current = null;
    setDraggingVertexIndex(null);
    setHoveredToolboxAction(null);
    setHoveredPolygonId(null);

    if (isMeasureTool && measurementPoints.length === 1) {
      setMeasurementHoverPoint(null);
    }
  };

  const handleWheel = (e) => {
    if (!isPanZoomTool) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.2 : -0.2;
    setZoom((z) => clampZoom(z + delta));
  };

  const handleSavePolygon = async () => {
    if (!selectedPolygonId && points.length > 2 && polygonClosed) {
      // Saving a new polygon
      const newPolygon = {
        id: Date.now(), // unique ID
        points: [...points],
        color: polygonColor,
        ...formData,
      };

      await dispatch(addRoom(buildRoomPayload(newPolygon)));
      await handleLoadRoomsFromBackend();
    } else if (selectedPolygonId) {
      // Updating an existing polygon
      const targetRoom = savedPolygons.find(
        (room) => room.id === selectedPolygonId,
      );
      if (!targetRoom) return;

      const nextPoints =
        points.length > 2 && polygonClosed
          ? [...points]
          : editablePolygonPoints.length >= 3
            ? editablePolygonPoints
            : [];

      if (nextPoints.length < 3) {
        return;
      }

      const updatedMeasurements = calculatePolygonMeasurements(
        nextPoints,
        resolution,
      );

      const updatedPolygon = {
        ...targetRoom,
        ...formData,
        ...updatedMeasurements,
        points: nextPoints,
      };

      if (targetRoom.persisted === false) {
        setSavedPolygons((prev) =>
          prev.map((room) =>
            room.id === selectedPolygonId ? updatedPolygon : room,
          ),
        );
      } else {
        try {
          await dispatch(
            updateRoomById(selectedPolygonId, buildRoomPayload(updatedPolygon)),
          );
          await handleLoadRoomsFromBackend();
        } catch (error) {
          onError?.(
            "Unable to update room. Please contact administrator for support.",
          );
          return;
        }
      }
    } else {
      return;
    }

    // Reset states
    setPoints([]);
    setPolygonClosed(false);
    setShowForm(false);
    setFormData(createInitialFormData());
    setSelectedPolygonId(null);
    setHoveredPolygonId(null);
    setEditablePolygonPoints([]);
    setDraggingVertexIndex(null);
    setIsReplacingSelectedPolygon(false);
  };

  const handleDeletePolygon = async (polygonId) => {
    const targetId = polygonId || selectedPolygonId;
    if (!targetId) return;

    const targetRoom = savedPolygons.find((room) => room.id === targetId);

    try {
      if (targetRoom?.persisted !== false) {
        await dispatch(deleteRoomById(targetId));
      }
    } catch (error) {
      onError?.(
        "Unable to delete room. Please contact administrator for support.",
      );
      return;
    }

    setSavedPolygons((prev) => prev.filter((p) => p.id !== targetId));

    if (targetId === selectedPolygonId) {
      setSelectedPolygonId(null);
      setHoveredPolygonId(null);
      setHoveredToolboxAction(null);
      setSelectedRoomToolMode(null);
      setFormData(createInitialFormData());
      setShowForm(false);
      setEditablePolygonPoints([]);
      setDraggingVertexIndex(null);
      setIsReplacingSelectedPolygon(false);
    }
  };

  const handleToggleRoomVisibility = async (polygonId) => {
    if (!polygonId) return;

    const prevPolygons = savedPolygons;

    let hiddenAfterToggle = false;

    const updatedPolygons = prevPolygons.map((polygon) => {
      if (polygon.id !== polygonId) return polygon;

      hiddenAfterToggle = !polygon.hidden;

      return {
        ...polygon,
        hidden: hiddenAfterToggle,
      };
    });

    // Optimistically update UI
    setSavedPolygons(updatedPolygons);

    if (hiddenAfterToggle) {
      setHoveredPolygonId((currentId) => (currentId === polygonId ? null : currentId));
      setHoveredToolboxAction(null);
      setSelectedRoomToolMode((currentMode) => (selectedPolygonId === polygonId ? null : currentMode));
      setSelectedPolygonId((currentId) => (currentId === polygonId ? null : currentId));
      setShowForm((currentShowForm) => (selectedPolygonId === polygonId ? false : currentShowForm));
      setFormData((currentFormData) => (selectedPolygonId === polygonId ? createInitialFormData() : currentFormData));
    }

    // If the room is persisted on backend, persist the hidden flag change
    const target = updatedPolygons.find((p) => p.id === polygonId);
    if (!target) return;

    // Do not call backend for imported/unpersisted rooms
    if (target.persisted === false) return;

    try {
      await dispatch(updateRoomById(polygonId, buildRoomPayload(target)));
      // refresh backend state to ensure consistency
      await handleLoadRoomsFromBackend();
    } catch (err) {
      // revert optimistic update on error
      setSavedPolygons(prevPolygons);
      onError?.("Unable to update room visibility. Please contact administrator.");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ["area", "width", "height"];

    let newValue = value;

    if (name === "active") {
      // normalize select value ("1"/"0") to numeric 1/0
      newValue = value === "1" || value === 1 || value === true ? 1 : 0;
    } else if (name === "highTouchAreas") {
      // checkbox or boolean-like input
      newValue = value === "true" || value === true || value === "1" || value === 1;
    } else if (numericFields.includes(name)) {
      newValue = Number(value) || 0;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleClearPolygon = () => {
    setPoints([]);
    setPolygonClosed(false);
    setPolygonColor(null);
    setHoverFirst(false);
    setDraggingVertexIndex(null);

    if (selectedPolygonId) {
      setEditablePolygonPoints([]);
      setIsReplacingSelectedPolygon(true);
    }

    setFormData((prev) => ({
      ...prev,
      width: 0,
      height: 0,
      area: 0,
    }));
  };

  const handleRequestDeletePolygon = (polygonOrId) => {
    const targetId =
      typeof polygonOrId === "object"
        ? polygonOrId?.id
        : polygonOrId || selectedPolygonId;
    if (!targetId) return;

    const targetRoom = savedPolygons.find((room) => room.id === targetId);
    if (!targetRoom) return;

    setRoomPendingDelete(targetRoom);
  };

  const handleCancelDeleteRequest = () => {
    setRoomPendingDelete(null);
  };

  const handleConfirmDeleteRequest = async () => {
    if (!roomPendingDelete?.id) return;
    await handleDeletePolygon(roomPendingDelete.id);
    setRoomPendingDelete(null);
  };

  const handleCreateNewRoom = () => {
    setPoints([]);
    setPolygonClosed(false);
    setPolygonColor(null);
    setSelectedPolygonId(null);
    setHoveredPolygonId(null);
    setEditablePolygonPoints([]);
    setDraggingVertexIndex(null);
    setHoveredToolboxAction(null);
    setSelectedRoomToolMode(null);
    setIsReplacingSelectedPolygon(false);
    setFormData(createInitialFormData());
    setShowForm(true);
    setSelected2DTool("draw");
  };

  const handleCancelForm = () => {
    setPoints([]);
    setPolygonClosed(false);
    setSelectedPolygonId(null);
    setHoveredPolygonId(null);
    setEditablePolygonPoints([]);
    setDraggingVertexIndex(null);
    setHoveredToolboxAction(null);
    setSelectedRoomToolMode(null);
    setIsReplacingSelectedPolygon(false);
    setFormData(createInitialFormData());
    setShowForm(false);
  };

  const handleClearMeasurement = () => {
    setMeasurementPoints([]);
    setMeasurementHoverPoint(null);
  };

  const hasDrawnPolygon = points.length > 2 && polygonClosed;
  const hasEditablePolygon =
    !!selectedPolygonId &&
    !isReplacingSelectedPolygon &&
    editablePolygonPoints.length >= 3;
  const canSaveRoom = hasDrawnPolygon || hasEditablePolygon;
  const hasUnsavedPolygon = points.length > 0 && !selectedPolygonId;

  const handleSelectRoom = (polygonId) => {
    const target = savedPolygons.find((polygon) => polygon.id === polygonId);
    if (!target) return;

    setSelectedPolygonId(target.id);
    setHoveredPolygonId(target.id);
    setHoveredToolboxAction(null);
    setSelectedRoomToolMode(null);
    setFormData(mapPolygonToFormData(target));
    setShowForm(true);
    setPoints([]);
    setPolygonClosed(false);
    setEditablePolygonPoints(target.points);
    setDraggingVertexIndex(null);
    setIsReplacingSelectedPolygon(false);
  };

  const handleImportRooms = (importedRooms) => {
    if (!Array.isArray(importedRooms) || importedRooms.length === 0) return;

    const baseId = Date.now();
    const normalizedRooms = importedRooms
      .map((room, index) => ({
        id: baseId + index,
        roomName: room.roomName || "Untitled",
        roomType: room.roomType || "",
        floorType: room.floorType || "",
        carpetType: room.carpetType || "",
        fixedFurniture: room.fixedFurniture || "",
        highTouchAreas: room.highTouchAreas || false,
        notes: room.notes || "",
        width: Number(room.width) || 0,
        height: Number(room.height) || 0,
        area: Number(room.area) || 0,
        hidden: Boolean(room.hidden),
        points: Array.isArray(room.points) ? room.points : [],
        persisted: false,
        color: room.color || randomColor(),
      }))
      .filter((room) => room.points.length >= 3);

    if (normalizedRooms.length === 0) return;

    setSavedPolygons((prev) => [...prev, ...normalizedRooms]);
    setPoints([]);
    setPolygonClosed(false);
    setPolygonColor(null);
    setSelectedPolygonId(null);
    setHoveredPolygonId(null);
    setEditablePolygonPoints([]);
    setDraggingVertexIndex(null);
    setHoveredToolboxAction(null);
    setSelectedRoomToolMode(null);
    setShowForm(false);
    setFormData(createInitialFormData());
    setIsReplacingSelectedPolygon(false);
  };

  const handleLoadRoomsFromBackend = async () => {
    if (!floorID) return 0;

    try {
      const backendRooms = await dispatch(getRoomsByFloor(floorID, scanJobId));

      console.log("test backend rooms", backendRooms);
      const normalizedRooms = (backendRooms || [])
        .map((room) => ({
          id: Number(room.id) || Date.now() + Math.random(),
          roomName: room.room || "Untitled",
          roomType: room.room_type || "",
          floorType: room.floor_type || "",
          carpetType: room.carpet_type || "",
          fixedFurniture: room.fixed_furniture || "",
          highTouchAreas: room.high_touch_area || false,
          notes: room.note || "",
          area: Number(room.room_area) || 0,
          width: 0,
          height: 0,
          points: Array.isArray(room.points) ? room.points : [],
          persisted: true,
          color: randomColor(),
          floorLevel: room.floor_level || "",
          areaType: room.area_type || "",
          custodialSpaceType: room.custodial_space_type || "",
          custodialSpace:room.custodial_space || "",
          locationID: room.location_id || "",
          custodialPriority: room.custodial_priority || "",
          buildingCode: room.building_code || "",
          active: (room.active === true || room.active === 1 || room.active === "1") ? 1 : 0,
          custodialTemplateCode: room.custodial_template_code || "",
          hidden: room.hidden ?? 0
        }))
        .filter((room) => room.points.length >= 3);

      setSavedPolygons(normalizedRooms);
      setPoints([]);
      setPolygonClosed(false);
      setPolygonColor(null);
      setSelectedPolygonId(null);
      setHoveredPolygonId(null);
      setEditablePolygonPoints([]);
      setDraggingVertexIndex(null);
      setHoveredToolboxAction(null);
      setSelectedRoomToolMode(null);
      setIsReplacingSelectedPolygon(false);
      setShowForm(false);
      setFormData(createInitialFormData());

      if (normalizedRooms.length === 0) {
        onError?.("No room found for this scan.");
      } else {
        onError?.("");
      }

      return normalizedRooms.length;
    } catch (error) {
      onError?.(
        "Unable to load rooms. Please contact administrator for support.",
      );
      throw error;
    }
  };

  return (
    <>
      <div className="d-flex gap-2 w-100 h-100">
        {/* Left Section */}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            height: "100vh",
            position: "relative",
            border: "1px solid #ccc",
            overflow: "hidden",
          }}
          onWheel={handleWheel}
        >
          {/* Left Label */}
          {/* <div
            style={{
              minWidth: 300,
              borderRadius: 6,
              padding: "8px 14px",
              position: "absolute",
              fontSize: 12,
              fontWeight: 500,
              top: 50,
              left: 150,
              zIndex: 3000,
              background: "#eef2f7",
              color: "black",
              opacity: 0.8,
            }}
          >
            <h4>{buildingName}</h4>

            <h4>{floorName}</h4>
          </div> */}

          <canvas
            ref={mapRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{ position: "absolute", inset: 0 }}
          />
          <canvas
            ref={overlayRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              position: "absolute",
              inset: 0,
              cursor:
                isDrawTool || isMeasureTool
                  ? "crosshair"
                  : isPanZoomTool
                    ? isDragging
                      ? "grabbing"
                      : "grab"
                    : hoveredToolboxAction
                      ? "pointer"
                      : hoveredPolygonId && (isHandTool || isEditTool)
                        ? "pointer"
                        : draggingVertexIndex !== null
                          ? "grabbing"
                          : "default",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          />
        </div>
        {/* Summary Box */}
        {/* <div
          style={{
            minWidth: 360,
            borderRadius: 6,
            padding: "8px 14px",
            position: "absolute",
            fontSize: 12,
            fontWeight: 500,
            top: 50,
            right: 400,
            zIndex: 3000,
            background: "#eef2f7",
            color: "black",
            opacity: 0.8,
          }}
        >
          <h2>Summary</h2>
          <p>Total Rooms: {savedPolygons.length}</p>
          <p>
            Total Area:{" "}
            {savedPolygons
              .reduce((sum, room) => sum + (room.area || 0), 0)
              .toFixed(1)}{" "}
            SQFT
          </p>
          <div className="mb-3">
            <div>{"Room < 100 SQFT: 10"}</div>
            <div>{"Room < 200 SQFT: 15"}</div>
            <div>{"Room < 400 SQFT: 30"}</div>
          </div>

          <div className="w-100 d-flex gap-2 mb-3">
            <span>Cost per SQFT $: </span>
            <input
              value={formData.costPerSqft || ""}
              onChange={handleFormChange}
              name="costPerSqft"
              className="form-control form-control-sm"
              type="number"
            />
          </div>
          <div className="w-100 d-flex gap-2 mb-3">
            <span> Total Labor $: </span>
            <input
              disabled
              value={(
                savedPolygons.reduce((sum, room) => sum + (room.area || 0), 0) *
                formData.costPerSqft
              ).toFixed(2)}
              onChange={handleFormChange}
              name="totalLabor"
              className="form-control form-control-sm"
              type="number"
            />
          </div>
          <div className="w-100 d-flex gap-2 mb-3">
            <span>Total Materials $: </span>
            <input
              value={formData.totalMaterials || ""}
              onChange={handleFormChange}
              name="totalMaterials"
              className="form-control form-control-sm"
              type="number"
            />
          </div>

          <div className="w-100 d-flex gap-2 mb-3">
            <span>Sales Tax %: </span>
            <input
              value={formData.salesTax || ""}
              onChange={handleFormChange}
              name="salesTax"
              className="form-control form-control-sm"
              type="number"
            />
          </div>

          <div className="w-100 d-flex gap-2 mb-3">
            <span>TOTAL: </span>
            <div>
              $
              {(
                (savedPolygons.reduce(
                  (sum, room) => sum + (room.area || 0),
                  0,
                ) *
                  (Number(formData.costPerSqft) || 0) +
                  Number(formData.totalMaterials || 0)) *
                (1 + Number(formData.salesTax || 0) / 100)
              ).toFixed(2)}
            </div>
          </div>
        </div> */}

        {/* Right Section */}
        <Fusion2DToolbar
          showForm={showForm}
          formData={formData}
          onFormChange={handleFormChange}
          onSaveRoom={handleSavePolygon}
          isEditing={Boolean(selectedPolygonId)}
          onCancel={handleCancelForm}
          onCreateNewRoom={handleCreateNewRoom}
          onDeleteRoom={handleRequestDeletePolygon}
          onToggleRoomVisibility={handleToggleRoomVisibility}
          onSelectRoom={handleSelectRoom}
          selectedPolygonId={selectedPolygonId}
          canSave={canSaveRoom}
          hasUnsavedPolygon={hasUnsavedPolygon}
          savedPolygons={savedPolygons}
          onImportRooms={handleImportRooms}
          onLoadRooms={handleLoadRoomsFromBackend}
          selectedTool={selectedTool}
          buildingName={buildingName}
          floorName={floorName}
        />

        {roomPendingDelete && (
          <div
            onClick={handleCancelDeleteRequest}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17, 33, 56, 0.28)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              zIndex: 1400,
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 500,
                background: "#ffffff",
                borderRadius: 16,
                boxShadow: "0 20px 50px rgba(17, 33, 56, 0.18)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#112138",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "20px 22px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      color: "#ffffff",
                      flexShrink: 0,
                    }}
                  >
                    <DeleteForeverRoundedIcon fontSize="large" />
                  </span>
                  <div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        lineHeight: 1.2,
                      }}
                    >
                      Delete Room
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.9,
                        marginTop: 4,
                      }}
                    >
                      Review the details before confirming this action
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCancelDeleteRequest}
                  style={{
                    border: "none",
                    background: "rgba(255, 255, 255, 0.08)",
                    color: "#ffffff",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    flexShrink: 0,
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </button>
              </div>
              <div style={{ padding: "24px 24px 0" }}>
                <div
                  style={{
                    border: "1px solid #e6ecf4",
                    borderRadius: 12,
                    background: "#f8fafc",
                    padding: "16px 18px",
                    marginBottom: 18,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#60738f",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 8,
                    }}
                  >
                    Room selected
                  </div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: "#1f2a44",
                      lineHeight: 1.3,
                    }}
                  >
                    {roomPendingDelete.roomName || "Untitled"}
                  </div>
                  {Number.isFinite(roomPendingDelete.area) && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#60738f",
                        marginTop: 4,
                      }}
                    >
                      {Number(roomPendingDelete.area).toFixed(1)} SQFT
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    border: "1px solid #ffcbc0",
                    borderRadius: 12,
                    background: "#fff7f7",
                    padding: "14px 16px",
                    marginBottom: 18,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#a54545",
                        marginBottom: 4,
                      }}
                    >
                      Permanent deletion
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#a54545",
                        lineHeight: 1.55,
                      }}
                    >
                      Once deleted, this room boundary and its saved data cannot
                      be restored.
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  padding: "16px 24px 24px",
                  borderTop: "1px solid #e6ecf4",
                  background: "#f8fafc",
                }}
              >
                <button
                  type="button"
                  onClick={handleCancelDeleteRequest}
                  style={{
                    border: "1px solid #d6dce5",
                    borderRadius: 10,
                    padding: "10px 16px",
                    minWidth: 96,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#4e5d78",
                    background: "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteRequest}
                  style={{
                    border: "none",
                    borderRadius: 10,
                    padding: "10px 18px",
                    minWidth: 108,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#ffffff",
                    background: "#b42318",
                    boxShadow: "0 4px 12px rgba(180, 35, 24, 0.24)",
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Fusion2DModel;
